/**
 * Created by Ning Jiang on 10/10/2018.
 */

import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {BitmapWinCountUp} from "@nolimitcity/slot-game/bin/game/winpresentation/countup/BitmapWinCountUp";
import {Elastic, Power1, Timeline, TimelineLite, TweenLite} from "gsap";
import {WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {BitmapTextStyleOptions} from "@nolimitcity/slot-game/bin/core/text/BitmapTextStyleOptions";
import {INolimitAudioPlayer} from "@nolimitcity/slot-audio/bin/INolimitAudioPlayer";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {MonkeysGoldSoundConfig} from "../../MonkeysGoldSoundConfig";
import {BitmapCountUp} from "@nolimitcity/slot-game/bin/core/component/BitmapCountUp";

export class MonkeysGoldInitialWinCountUp extends BitmapWinCountUp {

    private _isCountUpSoundPlaying:boolean = false;
    private _hasCountUpEndSoundPlayed:boolean = false;
    private _currentWinRatio:WinRatioType;
    private _countUpAnimationTL:TimelineLite;

    constructor(bitmapStyle:BitmapTextStyleOptions) {
        super(bitmapStyle);
    }

    private getScale(multiplier:number):number {
        return 1 + (multiplier - 1) * 0.1;
    }

    public getCountUpAnimation(from:number, to:number, duration:number, multiplier:number, winType:WinRatioType):Timeline {
        if(this._countUpAnimationTL && this._countUpAnimationTL.isActive()) {
            this._countUpAnimationTL.pause();
            this._countUpAnimationTL.kill();
        }
        this._countUpAnimationTL = new TimelineLite();
        const oriNumber:number = MathHelper.floorToDecimals(to / multiplier, 2);
        this._countUpAnimationTL.add([
            () => {
                this._countUp.maxTextWidth = GameConfig.instance.REEL_AREA_WIDTH * 0.9 / this.getScale(multiplier);
                this._countUp.visible = true;
                TweenLite.to(this._countUp, 0.3, {alpha : 1});
                this._countUpContainer.scale.set(1, 1);
                if(duration > 0.01) {
                    //EventHandler.dispatchEvent(new GameEvent(SoundEvent.PLAY_SOUND_EFFECT, BonusBunniesSoundConfig.instance.COUNTUP));
                    this._isCountUpSoundPlaying = true;
                }
            },
            this._countUp.getCountUpAnimation(from, oriNumber, {duration : duration, ease : Power1.easeIn})
        ]);

        if(multiplier > 1) {
            this._countUpAnimationTL.add(() => (<INolimitAudioPlayer>SlotGame.sound).stopTrack(MonkeysGoldSoundConfig.instance.COUNTUP));
            for(let m:number = 1; m <= multiplier; m++) {
                const showNumber:number = oriNumber * m;
                const scale:number = this.getScale(m);
                this._countUpAnimationTL.add([
                    () => {
                        if(m == multiplier) {
                            if(winType == WinRatioType.TINY) {
                                (<INolimitAudioPlayer>SlotGame.sound).playTrack({name : (MonkeysGoldSoundConfig.instance.COUNTUP_MULTIPLIER)});
                            }
                            (<INolimitAudioPlayer>SlotGame.sound).playTrack({name : (this.getCountupStopSound(winType))});
                        } else {
                            (<INolimitAudioPlayer>SlotGame.sound).playTrack({name : (MonkeysGoldSoundConfig.instance.COUNTUP_MULTIPLIER)});
                        }
                    },
                    this._countUp.getCountUpAnimation(showNumber, showNumber, {duration : 0.01}),
                    TweenLite.to(this._countUpContainer.scale, 0.3,
                        {
                            x : scale,
                            y : scale,
                            ease : Elastic.easeOut.config(2, 1)
                        }),
                ]);
                this._countUpAnimationTL.add(TweenLite.to(this, 0.3, {}));
            }
        }

        return this._countUpAnimationTL;
    }

    private getCountupStopSound(winType:WinRatioType):string {
        switch(winType) {
            case WinRatioType.LARGE :
                return MonkeysGoldSoundConfig.instance.COUNTUP_ENDS_LARGE;

            case WinRatioType.MEDIUM :
                return MonkeysGoldSoundConfig.instance.COUNTUP_ENDS_MEDIUM;

            case WinRatioType.SMALL :
                return MonkeysGoldSoundConfig.instance.COUNTUP_ENDS_SMALL;

            case WinRatioType.TINY :
                return MonkeysGoldSoundConfig.instance.COUNTUP_ENDS_TINY;

            default :
                return "";
        }
    }

    public abort(endValue:number, isAborted:boolean, multiplier:number, isBigWin:boolean):Timeline | null {
        if(isBigWin) {
            return null;
        }

        if(this._countUpAnimationTL && this._countUpAnimationTL.isActive()) {
            this._countUpAnimationTL.pause();
            this._countUpAnimationTL.kill();
        }

        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                this._countUp.maxTextWidth = GameConfig.instance.REEL_AREA_WIDTH * 0.9 / this.getScale(multiplier);
                this._countUp.alpha = 1;
                this._countUp.visible = true;
            },
            this._countUp.getCountUpAnimation(endValue, endValue, {duration : 0.01})
        ]);
        if(isAborted == true) {
            if(multiplier > 1) {
                const scale:number = this.getScale(multiplier);
                timeline.add(TweenLite.to(this._countUpContainer.scale, 0.2, {
                    x : scale,
                    y : scale,
                    ease : Elastic.easeOut.config(2, 1)
                }), 0)
            }
        }

        timeline.add(TweenLite.to(this._countUp, 0.5, {alpha : 0}), `+=0.6`); // stay a while after count up.

        return timeline;
    }

    protected getAbortAnimation(endValue:number, multiplier:number):Timeline {
        const timeline:TimelineLite = new TimelineLite();

        return timeline;
    }

    public getEndingAnimation(endValue:number):Timeline {
        const timeline:TimelineLite = new TimelineLite({
            onComplete : () => {
                this._isCountUpSoundPlaying = false;
                this._hasCountUpEndSoundPlayed = false;
            }
        });
        timeline.add(this._countUp.getCountUpAnimation(endValue, endValue, {duration : 0.01}));
        timeline.add(TweenLite.to(this._countUp, 0.5, {alpha : 0}), `+=0.6`); // stay a while after count up.

        return timeline;
    }

    set currentWinRatio(value:WinRatioType) {
        this._currentWinRatio = value;
    }

    public getBitmapCountUp():BitmapCountUp {
        return this._countUp;
    }
}